import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { fold } from "@/lib/search";

const matches = (option, value) => {
  const tokens = fold(value).split(/\s+/).filter(Boolean);
  return tokens.every((t) => fold(option).includes(t));
};

export const ModelPicker = ({ value, options, onChange, placeholder, className, selectOnly = false, groupOf, testid = "order-model" }) => {
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState(false);
  const box = useRef(null);
  const exact = options.some((o) => fold(o) === fold(value));
  const list = !selectOnly && typing && value && !exact ? options.filter((o) => matches(o, value)) : options;

  const pick = (o) => {
    onChange(o);
    setTyping(false);
    setOpen(false);
  };

  // No iPhone (Safari) o toque não dá foco ao botão, então fechar no blur sumia com a lista antes do clique.
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);

  return (
    <div ref={box} onKeyDown={(e) => e.key === "Escape" && setOpen(false)} className="relative">
      <input data-testid={`${testid}-input`} value={value} placeholder={placeholder} autoComplete="off" role="combobox" aria-expanded={open}
        readOnly={selectOnly} inputMode={selectOnly ? "none" : undefined}
        onClick={() => selectOnly && setOpen(true)}
        onFocus={() => { setTyping(false); setOpen(true); }}
        onChange={(e) => { onChange(e.target.value); setTyping(true); setOpen(true); }}
        className={`${className} pr-11 ${selectOnly ? "cursor-pointer caret-transparent" : ""}`} />
      <button type="button" data-testid={`${testid}-toggle`} aria-label="Ver todos os modelos" onClick={() => { setTyping(false); setOpen((o) => !o); }}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-zinc-400 hover:text-white">
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && list.length > 0 && (
        <ul data-testid={`${testid}-options`} data-lenis-prevent role="listbox"
          className="absolute inset-x-0 top-full z-30 mt-2 max-h-80 overflow-y-auto rounded-2xl border border-white/10 bg-[#141416] p-1.5 shadow-2xl">
          {list.map((o, i) => {
            const group = groupOf?.(o);
            const header = group && group !== groupOf(list[i - 1]);
            return (
              <li key={o}>
                {header && <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400 first:pt-1">{group}</p>}
                <button type="button" data-testid={`${testid}-option-${o.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} onClick={() => pick(o)}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/10 ${fold(o) === fold(value) ? "text-white" : "text-zinc-300"}`}>
                  {o}
                  {fold(o) === fold(value) && <Check className="h-4 w-4 text-emerald-300" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
